/**
 * Озвучення англійських фраз через Web Speech API.
 *
 * НАВІЩО ЦЕ ІСНУЄ
 * ---------------------------------------------------------------------------
 * Навичка «Аудіювання» перевіряє, чи впізнає дитина слово НА СЛУХ. Рушій
 * віддає такі питання з полем `passage` вигляду «🔊 have breakfast», а
 * інструкція в `prompt` дублює той самий рядок з нового рядка — так задумано
 * в `packages/engine/src/generators/english/listening.ts`, бо id питання
 * рахується з prompt, і без тексту фрази два різні аудіозавдання з однаковою
 * інструкцією отримали б однаковий id.
 *
 * Клієнт зобовʼязаний цей рядок СХОВАТИ й показати замість нього кнопку.
 * До появи цього модуля він цього не робив: `QuizScreen` малював `passage`
 * звичайним абзацом, і дитина бачила «have breakfast» на екрані двічі —
 * у полі умови й усередині інструкції. Завдання переставало бути
 * аудіюванням і ставало читанням, причому з відповіддю на видноті.
 *
 * ЧОМУ ОЗВУЧЕННЯ НЕ СЛУХАЄТЬСЯ ЗАГАЛЬНОГО ПЕРЕМИКАЧА ЗВУКУ
 * ---------------------------------------------------------------------------
 * `soundEnabled` у кабінеті батьків керує ЕФЕКТАМИ: фанфарами за правильну
 * відповідь, клацанням кнопок. Це прикраса. Мова тут — не прикраса, а сам
 * зміст завдання: без неї питання не має розвʼязку. Якби перемикач глушив і
 * її, ціла навичка тихо перетворювалась би на читання, і ніхто б не помітив —
 * рівно та історія, що вже трапилась із файлами звуків (див. sound.ts).
 * Тому мова звучить завжди, але ЛИШЕ у відповідь на дотик дитини до кнопки:
 * жодного автоматичного шуму на весь автобус.
 *
 * ЩО РОБИТИ, КОЛИ ГОЛОСУ НЕМАЄ
 * ---------------------------------------------------------------------------
 * Web Speech є не скрізь: старий браузер, вимкнений синтез, відсутній
 * англійський голос у системі. Мовчазний провал тут неприпустимий — дитина
 * лишилась би з питанням без жодного способу відповісти. Тому
 * `speechSupport()` дає явний стан, а екран у разі «немає» показує текст
 * фрази. Завдання деградує до читання — це гірше за аудіювання, але
 * незрівнянно краще за глухий кут.
 */

/** Маркер, яким рушій позначає рядок для озвучення. */
export const SPEECH_MARKER = '🔊';

/** Мова синтезу. Британська вимова — та, якої вчать у школі. */
export const SPEECH_LANG = 'en-GB';

export type SpeechSupport = 'ready' | 'missing';

interface SpeechWindow {
  speechSynthesis?: SpeechSynthesis;
  SpeechSynthesisUtterance?: typeof SpeechSynthesisUtterance;
}

function api(): SpeechSynthesis | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as SpeechWindow;
  // Обидві половини потрібні разом: у деяких вбудованих оглядачах є обʼєкт
  // speechSynthesis, але немає конструктора висловлювання, і виклик падає.
  if (!w.speechSynthesis || typeof w.SpeechSynthesisUtterance !== 'function') return null;
  return w.speechSynthesis;
}

export function speechSupport(): SpeechSupport {
  return api() ? 'ready' : 'missing';
}

/**
 * Текст для озвучення з поля `passage`.
 *
 * Повертає `null`, якщо це звичайна умова (текст для читання, SVG-малюнок).
 * Маркер може бути з пробілом або без — рушій ставить «🔊 », але покладатися
 * на один пробіл не варто: контракт тримається на самому знаку.
 */
export function speechTextOf(passage: string | null | undefined): string | null {
  if (!passage) return null;
  const trimmed = passage.trim();
  if (!trimmed.startsWith(SPEECH_MARKER)) return null;
  const text = trimmed.slice(SPEECH_MARKER.length).trim();
  return text.length > 0 ? text : null;
}

/**
 * Інструкція без рядка, що дублює фразу.
 *
 * Рушій кладе фразу і в `passage`, і в `prompt` — друге лише заради
 * унікального id. На екрані вона не має зʼявитися жодного разу, тож звідси
 * викидаємо КОЖЕН рядок із маркером, а не лише останній.
 */
export function stripSpeechLines(prompt: string): string {
  return prompt
    .split('\n')
    .filter((line) => !line.trim().startsWith(SPEECH_MARKER))
    .join('\n')
    .trim();
}

/**
 * Голос для англійської.
 *
 * Точний збіг з en-GB кращий, але його часто немає: на багатьох Android
 * стоїть лише en-US. Будь-який англійський голос незрівнянно ближчий до
 * потрібного, ніж українська вимова англійських слів системним голосом
 * за замовчуванням, тому беремо перший-ліпший en-*.
 */
function pickVoice(synth: SpeechSynthesis): SpeechSynthesisVoice | null {
  const voices = synth.getVoices();
  if (voices.length === 0) return null;
  return (
    voices.find((v) => v.lang === SPEECH_LANG) ??
    voices.find((v) => v.lang.replace('_', '-').startsWith('en-')) ??
    null
  );
}

export interface SpeakOptions {
  /** Викликається, коли фраза дозвучала або озвучення зірвалось. */
  onEnd?: () => void;
}

/**
 * Промовити фразу. Попереднє озвучення обривається: дитина натиснула
 * «ще раз» саме тому, що не дочула, і слухати дві накладені фрази — гірше,
 * ніж одну.
 */
export function speak(text: string, opts: SpeakOptions = {}): void {
  const synth = api();
  if (!synth || text.trim().length === 0) {
    opts.onEnd?.();
    return;
  }

  synth.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = SPEECH_LANG;
  const voice = pickVoice(synth);
  if (voice) utterance.voice = voice;
  // Трохи повільніше за типову швидкість: дитина чує чужу мову, і 1.0
  // для неї зливається в суцільний звук.
  utterance.rate = 0.85;
  utterance.onend = () => opts.onEnd?.();
  utterance.onerror = () => opts.onEnd?.();

  synth.speak(utterance);
}

/** Обірвати озвучення. Потрібне при переході до наступного питання. */
export function stopSpeaking(): void {
  api()?.cancel();
}
